import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    createApi,
    fetchBaseQuery,
    retry,
} from "@reduxjs/toolkit/query/react";

import { initializedActions } from "../initial-app-slice";
import { RootState } from "../store";
import {
    ProductData,
    Product,
    User,
    ProjectData,
    Project,
    ProductInfo,
} from "../../models/models";
import {
    LicenseKeysCollection,
    GenerateKeysList,
} from "../../pages/KeyGenerator/model";
import {
    LicenseKeysMigratedQueryParams,
    MigratedLicenseData,
} from "../../pages/LicenseKeys/LicenseKeysMigrated/model";
import {
    LicenseData,
    FetchQuiresLicenseKeys,
    RemoveActivationLicense,
    UpdateActivationLimitLicense,
} from "../../pages/LicenseKeys/model";
import {
    MigratedProduct,
    CreateMigratedProduct,
    UpdateMigratedProduct,
    MigratedProductsResponse,
    FetchQuiresMigratedProducts,
    MigratedProductsDetails,
    FetchQuiresMigratedProductsDetails,
} from "../../pages/Products/MigratedProducts/model";
import { ProductsQueryParams } from "../../pages/Products/model";
import { ProjectQueryParams } from "../../pages/Projects/model";
import {
    FetchLicenseHistory,
    LicenseHistoryQueryParams,
} from "../../pages/LicenseKeys/LicenseKeyHistory/historyModel";
import {
    FetchHistoryService,
    FetchQuiresHistory,
} from "../../pages/History/model";
import {
    FetchNotActivatedLicenseKeys,
    LicenseKeysNotActivatedQueryParams,
} from "../../pages/LicenseKeys/Pages/LicenseKeysNotActivated/model";
import {
    FetchTransactions,
    TransactionsQueryParams,
} from "../../pages/Transactions/model";
import type { paths, components } from "./schema";

export type IoloLicenseInfoQueryParams = NonNullable<
    paths["/api/License/iolo-license-info"]["get"]["parameters"]["query"]
>;
export type IoloLicenseDetailInfo =
    components["schemas"]["IoloLicenseDetailInfoViewModel"];
export type IoloDetailInfo = components["schemas"]["IoloDetailInfo"];
export type IoloLicenseInfo =
    components["schemas"]["IoloLicenseInfoViewModel"];
export type UpdateIoloLicenseExpirationBody =
    components["schemas"]["UpdateIoloLicenseExpirationViewModel"];
export type IoloOperationSuccess =
    components["schemas"]["IoloOperationSuccessResponse"];
export type LicenseImportSuccess =
    components["schemas"]["LicenseImportSuccessResponse"];
export type AvailablePermissions =
    components["schemas"]["AvailablePermissionsViewModel"];

export type QueryPageParams = {
    pageNumber: number;
    pageSize: number;
};

enum Feature {
    PRODUCTS = "Products",
    LICENSE = "License",
    NEW_LICENSE = "NewLicense",
    USER = "User",
    PROJECTS = "Projects",
    MIGRATION = "Migration",
    HISTORY = "History",
    REPORT = "Report",
    TRANSACTIONS = "Transactions",
    IOLO_LICENSE = "IoloLicense",
}

const paramsSerializer = (params: Record<string, unknown>): string => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
            value.forEach((v) => {
                if (v !== undefined && v !== null) {
                    search.append(key, String(v));
                }
            });
        } else {
            search.append(key, String(value));
        }
    }
    return search.toString();
};

const baseQuery = fetchBaseQuery({
    baseUrl: `https://${
        process.env.REACT_APP_DEPLOY_PLATFORM === "stage" ? "stage-" : ""
    }license.adaware.com/api/`,
    paramsSerializer,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).initializeApp.token;
        if (token) {
            headers.set("Authorization", `Bearer ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = async (args, api, extraOptions) => {
    let result = await baseQuery(args, api, extraOptions);
    if (result.error && result.error.status === 401) {
        api.dispatch(initializedActions.initializedFiled());
    }
    return result;
};

const baseQueryWithRetry = retry(baseQueryWithReauth, { maxRetries: 2 });

export const api = createApi({
    reducerPath: "api",
    tagTypes: [
        Feature.PRODUCTS,
        Feature.LICENSE,
        Feature.NEW_LICENSE,
        Feature.USER,
        Feature.PROJECTS,
        Feature.MIGRATION,
        Feature.HISTORY,
        Feature.REPORT,
        Feature.TRANSACTIONS,
        Feature.IOLO_LICENSE,
    ],
    baseQuery: baseQueryWithRetry,
    refetchOnFocus: true,

    endpoints: (build) => ({
        getProductsList: build.query<ProductData, ProductsQueryParams>({
            query: (args) => ({
                url: `${Feature.PRODUCTS}/list`,
                params: { ...args },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.products.map((product) => ({
                              type: Feature.PRODUCTS as const,
                              id: String(product.id),
                          })),
                          { type: Feature.PRODUCTS, id: Feature.PRODUCTS },
                      ]
                    : [{ type: Feature.PRODUCTS, id: Feature.PRODUCTS }],
        }),

        updateProduct: build.mutation<Product, Partial<Product>>({
            query: (body) => ({
                url: `${Feature.PRODUCTS}/update`,
                method: "POST",
                body,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: Feature.PRODUCTS, id: String(id) },
            ],
        }),

        toggleProductStatus: build.mutation<
            { success: boolean },
            { id: number }
        >({
            query: ({ id }) => ({
                url: `${Feature.PRODUCTS}/set-status`,
                method: "POST",
                params: { id },
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: Feature.PRODUCTS, id: String(id) },
            ],
        }),

        createProduct: build.mutation<Product, Partial<Product>>({
            query: (body) => ({
                url: `${Feature.PRODUCTS}/create`,
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: Feature.PRODUCTS, id: Feature.PRODUCTS }],
        }),

        createMigratedProduct: build.mutation<
            MigratedProduct,
            Partial<CreateMigratedProduct>
        >({
            query: (body) => ({
                url: `${Feature.MIGRATION}/product-mapping`,
                method: "POST",
                body,
            }),
            invalidatesTags: [
                { type: Feature.MIGRATION, id: Feature.MIGRATION },
            ],
        }),

        getProductInfo: build.query<ProductInfo, { id: number }>({
            query: ({ id }) => ({
                url: `${Feature.PRODUCTS}/license-stats`,
                params: { id },
            }),
        }),

        updateMigratedProduct: build.mutation<
            MigratedProduct,
            Partial<UpdateMigratedProduct>
        >({
            query: (body) => ({
                url: `${Feature.MIGRATION}/product-update`,
                method: "POST",
                body,
            }),
            invalidatesTags: (result, error, id) => [
                { type: Feature.MIGRATION, id: String(id) },
            ],
        }),

        uploadCSVMigratedProducts: build.mutation<MigratedProduct, FormData>({
            query: (body) => ({
                url: `${Feature.MIGRATION}/product-csv-mapping`,
                method: "POST",
                body,
            }),
            invalidatesTags: [
                { type: Feature.MIGRATION, id: Feature.MIGRATION },
            ],
        }),

        getMigratedProductsList: build.query<
            MigratedProductsResponse,
            FetchQuiresMigratedProducts
        >({
            query: (args) => ({
                url: `${Feature.MIGRATION}/${Feature.PRODUCTS}`,
                params: { ...args },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.products.map((product) => ({
                              type: Feature.MIGRATION as const,
                              id: String(product.productId),
                          })),
                          { type: Feature.MIGRATION, id: Feature.MIGRATION },
                      ]
                    : [{ type: Feature.MIGRATION, id: Feature.MIGRATION }],
        }),

        getMigrationProductsDetails: build.query<
            MigratedProductsDetails,
            FetchQuiresMigratedProductsDetails
        >({
            query: (args) => ({
                url: `${Feature.MIGRATION}/products-details`,
                params: { ...args },
            }),
        }),

        deleteMigratedProduct: build.mutation<
            { success: boolean; id: number },
            string
        >({
            query: (id) => ({
                url: `${Feature.MIGRATION}/product-delete`,
                params: { id },
                method: "DELETE",
            }),
            invalidatesTags: (result, error, id) => [
                { type: Feature.MIGRATION, id: String(id) },
            ],
        }),

        getMigratedLicensesList: build.query<
            MigratedLicenseData,
            LicenseKeysMigratedQueryParams
        >({
            query: (args) => ({
                url: `${Feature.MIGRATION}/licenses`,
                params: { ...args },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.licenses.map((license) => ({
                              type: Feature.MIGRATION as const,
                              id: String(license.globalOrderID),
                          })),
                          { type: Feature.MIGRATION, id: Feature.MIGRATION },
                      ]
                    : [{ type: Feature.MIGRATION, id: Feature.MIGRATION }],
        }),

        getReportNotActivatedLicensesList: build.query<
            FetchNotActivatedLicenseKeys,
            LicenseKeysNotActivatedQueryParams
        >({
            query: (args) => ({
                url: `${Feature.REPORT}/unactivated-licenses`,
                params: { ...args },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.items.map((license) => ({
                              type: Feature.REPORT as const,
                              id: String(license.id),
                          })),
                          { type: Feature.REPORT, id: Feature.REPORT },
                      ]
                    : [{ type: Feature.REPORT, id: Feature.REPORT }],
        }),

        downloadCSVNotActivatedLicenses: build.mutation<
            Blob, // Тип результата должен быть Blob
            LicenseKeysNotActivatedQueryParams
        >({
            query: (args) => ({
                url: `${Feature.REPORT}/unactivated-licenses-csv`,
                params: { ...args },
                responseHandler: (response) => response.blob(), // Обработка ответа как Blob
            }),
            async onQueryStarted(args, { queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const url = URL.createObjectURL(data);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = "UnactivatedLicenses.csv";
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(url);
                } catch (error) {
                    console.error("Error downloading file:", error);
                }
            },
        }),

        uploadCSVMigratedLicense: build.mutation<MigratedProduct, FormData>({
            query: (body) => ({
                url: `${Feature.MIGRATION}/license-csv-mapping`,
                method: "POST",
                body,
            }),
            invalidatesTags: [
                { type: Feature.MIGRATION, id: Feature.MIGRATION },
                { type: Feature.LICENSE, id: Feature.LICENSE },
            ],
        }),

        deleteMigratedLicenses: build.mutation<
            { success: boolean; id: number },
            number[]
        >({
            query: (body) => ({
                url: `${Feature.MIGRATION}/license-delete`,
                method: "DELETE",
                body,
            }),
            invalidatesTags: [
                { type: Feature.MIGRATION, id: Feature.MIGRATION },
                { type: Feature.LICENSE, id: Feature.LICENSE },
            ],
        }),

        getListLicense: build.query<LicenseData, FetchQuiresLicenseKeys>({
            query: (quires) => ({
                url: `${Feature.LICENSE}/list`,
                params: { ...quires },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.detailedInfo.map(({ license }) => ({
                              type: Feature.LICENSE as const,
                              id: String(license.id),
                          })),
                          { type: Feature.LICENSE, id: Feature.LICENSE },
                      ]
                    : [{ type: Feature.LICENSE, id: Feature.LICENSE }],
        }),

        deleteProduct: build.mutation<{ success: boolean; id: number }, number>(
            {
                query: (id) => ({
                    url: `${Feature.PRODUCTS}/delete`,
                    params: { id },
                    method: "DELETE",
                }),
                invalidatesTags: (result, error, id) => [
                    { type: Feature.PRODUCTS, id: String(id) },
                ],
            }
        ),

        deleteProject: build.mutation<{ success: boolean; id: number }, number>(
            {
                query: (id) => ({
                    url: `${Feature.PROJECTS}/delete`,
                    params: { id },
                    method: "DELETE",
                }),
                invalidatesTags: (result, error, id) => [
                    { type: Feature.PROJECTS, id: String(id) },
                ],
            }
        ),

        deleteLicense: build.mutation<
            { success: boolean; idList: Array<number> },
            Array<number>
        >({
            query: (idList) => ({
                url: `${Feature.LICENSE}/delete`,
                method: "DELETE",
                body: idList,
            }),
            invalidatesTags: (result, error, idList) =>
                idList.map((id) => ({ type: Feature.LICENSE, id: String(id) })),
        }),

        gracePeriod: build.mutation<
            { success: boolean },
            { licenseKey: string }
        >({
            query: ({ licenseKey }) => ({
                url: `${Feature.LICENSE}/start-grace-period`,
                method: "POST",
                body: { licenseKey },
            }),
            invalidatesTags: [{ type: Feature.LICENSE, id: Feature.LICENSE }],
        }),

        expireLicense: build.mutation<
            { success: boolean; key: string },
            string
        >({
            query: (key) => ({
                url: `${Feature.LICENSE}/expire`,
                method: "POST",
                body: { licenseKey: key },
            }),
            invalidatesTags: [{ type: Feature.LICENSE, id: Feature.LICENSE }],
        }),

        updateActivationLimitLicense: build.mutation<
            { success: boolean },
            UpdateActivationLimitLicense
        >({
            query: (params) => ({
                url: `${Feature.LICENSE}/update-activation`,
                method: "POST",
                body: params,
            }),
            invalidatesTags: [{ type: Feature.LICENSE, id: Feature.LICENSE }],
        }),

        removeActivationsLicense: build.mutation<
            {
                success: boolean;
            },
            RemoveActivationLicense
        >({
            query: (params) => ({
                url: `${Feature.LICENSE}/remove-activations`,
                method: "POST",
                body: params,
            }),
            invalidatesTags: [{ type: Feature.LICENSE, id: Feature.LICENSE }],
        }),

        getHistoryLicense: build.query<
            FetchLicenseHistory[],
            LicenseHistoryQueryParams
        >({
            query: (args) => ({
                url: `${Feature.LICENSE}/history`,
                params: { ...args },
            }),
        }),

        getHistoryMigrationLicense: build.query<
            FetchLicenseHistory[],
            LicenseHistoryQueryParams
        >({
            query: (args) => ({
                url: `${Feature.MIGRATION}/history`,
                params: { ...args },
            }),
        }),

        deactivateLicense: build.mutation<
            { success: boolean },
            { state: boolean; key: string }
        >({
            query: ({ state, key }) => ({
                url: `${Feature.LICENSE}/deactivate`,
                params: { state, key },
                method: "POST",
            }),
            invalidatesTags: [{ type: Feature.LICENSE, id: Feature.LICENSE }],
        }),

        generateNewLicenseList: build.mutation<
            LicenseKeysCollection | { blob: Blob; filename: string },
            Partial<GenerateKeysList>
        >({
            query: (params) => ({
                url: `${Feature.LICENSE}/generate`,
                method: "POST",
                body: { ...params },
                headers: {
                    "Content-Type": "application/json-patch+json",
                },
                responseHandler: async (response) => {
                    const contentType = response.headers.get("Content-Type");
                    if (
                        contentType &&
                        contentType.includes("application/json; charset=utf-8")
                    ) {
                        return await response.json();
                    } else {
                        const blob = await response.blob();
                        const contentDisposition = response.headers.get(
                            "Content-Disposition"
                        );
                        let filename = "LicenseKeysAvanquest.txt";
                        if (contentDisposition) {
                            const match = contentDisposition.match(
                                /filename[^;=\n]*=([^;]*)/
                            );
                            if (match && match[1]) {
                                filename = decodeURIComponent(
                                    match[1].replace(/['"]/g, "")
                                );
                            }
                        }
                        return { blob, filename };
                    }
                },
            }),
            invalidatesTags: [{ type: Feature.LICENSE, id: Feature.LICENSE }],
        }),

        getUsersList: build.query<User[], "">({
            query: () => ({
                url: `${Feature.USER}/list`,
            }),
            providesTags: [{ type: Feature.USER, id: Feature.USER }],
        }),

        getAvailablePermissions: build.query<AvailablePermissions, void>({
            query: () => ({
                url: `${Feature.USER}/permissions`,
            }),
        }),

        // getLicenseInfo: build.query<LicenseInfoPanel, { licenseKey: string }>({
        //     query: ({ licenseKey }) => ({
        //         url: `${Feature.LICENSE}/info`,
        //         params: { licenseKey },
        //     }),
        // }),

        createUser: build.mutation<User, Partial<User>>({
            query: (body) => ({
                url: `${Feature.USER}/create`,
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: Feature.USER, id: Feature.USER }],
        }),

        updateUser: build.mutation<User, Partial<User>>({
            query: (body) => ({
                url: `${Feature.USER}/update`,
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: Feature.USER, id: Feature.USER }],
        }),

        deleteUser: build.mutation<{ success: boolean; id: number }, number>({
            query: (id) => ({
                url: `${Feature.USER}/delete`,
                params: { id },
                method: "DELETE",
            }),
            invalidatesTags: [{ type: Feature.USER, id: Feature.USER }],
        }),

        getProjectsList: build.query<ProjectData, ProjectQueryParams>({
            query: (arg) => ({
                url: `${Feature.PROJECTS}/list`,
                params: { ...arg },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.projects.map((project) => ({
                              type: Feature.PROJECTS as const,
                              id: String(project.id),
                          })),
                          { type: Feature.PROJECTS, id: Feature.PROJECTS },
                      ]
                    : [{ type: Feature.PROJECTS, id: Feature.PROJECTS }],
        }),

        createProject: build.mutation<Project, Partial<Project>>({
            query: (body) => ({
                url: `${Feature.PROJECTS}/create`,
                method: "POST",
                body,
            }),
            invalidatesTags: [{ type: Feature.PROJECTS, id: Feature.PROJECTS }],
        }),

        updateProject: build.mutation<Project, Partial<Project>>({
            query: (body) => ({
                url: `${Feature.PROJECTS}/update`,
                method: "POST",
                body,
            }),
            invalidatesTags: (result, error, { id }) => [
                { type: Feature.PROJECTS, id: String(id) },
            ],
        }),

        getHistory: build.query<FetchHistoryService, FetchQuiresHistory>({
            query: (quires) => ({
                url: `${Feature.HISTORY}/list`,
                params: { ...quires },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.histories.map(({ id }) => ({
                              type: Feature.HISTORY as const,
                              id: String(id),
                          })),
                          { type: Feature.HISTORY, id: Feature.HISTORY },
                      ]
                    : [{ type: Feature.HISTORY, id: Feature.HISTORY }],
        }),

        getTransactionsList: build.query<
            FetchTransactions,
            TransactionsQueryParams
        >({
            query: (args) => ({
                url: `${Feature.TRANSACTIONS}/list`,
                params: { ...args },
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.items.map((transaction) => ({
                              type: Feature.TRANSACTIONS as const,
                              id: String(transaction.id),
                          })),
                          {
                              type: Feature.TRANSACTIONS,
                              id: Feature.TRANSACTIONS,
                          },
                      ]
                    : [
                          {
                              type: Feature.TRANSACTIONS,
                              id: Feature.TRANSACTIONS,
                          },
                      ],
        }),

        retryTransactions: build.mutation<
            { success: boolean; retried: number },
            { ids: number[] }
        >({
            query: ({ ids }) => ({
                url: `${Feature.TRANSACTIONS}/retry`,
                method: "POST",
                body: { ids },
            }),
            invalidatesTags: [
                { type: Feature.TRANSACTIONS, id: Feature.TRANSACTIONS },
            ],
        }),

        retryAllFailedTransactions: build.mutation<
            { success: boolean; retried: number },
            TransactionsQueryParams
        >({
            query: (params) => ({
                url: `${Feature.TRANSACTIONS}/retry-all-failed`,
                method: "POST",
                body: params,
            }),
            invalidatesTags: [
                { type: Feature.TRANSACTIONS, id: Feature.TRANSACTIONS },
            ],
        }),

        getIoloLicenseInfo: build.query<
            IoloLicenseDetailInfo,
            IoloLicenseInfoQueryParams
        >({
            query: (args) => ({
                url: `${Feature.LICENSE}/iolo-license-info`,
                params: { ...args },
            }),
            providesTags: (result) =>
                result?.detailedInfo
                    ? [
                          ...result.detailedInfo.map(({ ioloInfo }) => ({
                              type: Feature.IOLO_LICENSE as const,
                              id: ioloInfo?.licenseKey ?? "unknown",
                          })),
                          {
                              type: Feature.IOLO_LICENSE,
                              id: Feature.IOLO_LICENSE,
                          },
                      ]
                    : [
                          {
                              type: Feature.IOLO_LICENSE,
                              id: Feature.IOLO_LICENSE,
                          },
                      ],
        }),

        disableIoloLicense: build.mutation<IoloOperationSuccess, string>({
            query: (licenseKey) => ({
                url: `${Feature.LICENSE}/iolo-disable`,
                method: "PATCH",
                params: { licenseKey },
            }),
            invalidatesTags: [
                { type: Feature.IOLO_LICENSE, id: Feature.IOLO_LICENSE },
            ],
        }),

        updateIoloLicenseExpiration: build.mutation<
            IoloOperationSuccess,
            UpdateIoloLicenseExpirationBody
        >({
            query: (body) => ({
                url: `${Feature.LICENSE}/iolo-update`,
                method: "PATCH",
                body,
                headers: {
                    "Content-Type": "application/json-patch+json",
                },
            }),
            invalidatesTags: [
                { type: Feature.IOLO_LICENSE, id: Feature.IOLO_LICENSE },
            ],
        }),

        importIoloLicense: build.mutation<LicenseImportSuccess, FormData>({
            query: (body) => ({
                url: `Import/import-license`,
                method: "POST",
                body,
            }),
            invalidatesTags: [
                { type: Feature.IOLO_LICENSE, id: Feature.IOLO_LICENSE },
            ],
        }),
    }),
});

export const {
    useGetReportNotActivatedLicensesListQuery,
    useGetProductsListQuery,
    useGetProductInfoQuery,
    useRemoveActivationsLicenseMutation,
    useUpdateActivationLimitLicenseMutation,
    useGetMigratedProductsListQuery,
    useGetMigrationProductsDetailsQuery,
    useCreateMigratedProductMutation,
    useUpdateMigratedProductMutation,
    useDeleteMigratedProductMutation,
    useUploadCSVMigratedProductsMutation,
    useGetMigratedLicensesListQuery,
    useGetHistoryMigrationLicenseQuery,
    useUploadCSVMigratedLicenseMutation,
    useDeleteMigratedLicensesMutation,
    useDeleteProductMutation,
    useUpdateProductMutation,
    useToggleProductStatusMutation,
    useCreateProductMutation,
    useGetListLicenseQuery,
    useDeleteLicenseMutation,
    useExpireLicenseMutation,
    useGetHistoryLicenseQuery,
    useDeactivateLicenseMutation,
    useGenerateNewLicenseListMutation,
    useGetUsersListQuery,
    useGetAvailablePermissionsQuery,
    useDeleteUserMutation,
    useUpdateUserMutation,
    useCreateUserMutation,
    useGetProjectsListQuery,
    useCreateProjectMutation,
    useUpdateProjectMutation,
    useDeleteProjectMutation,
    useGracePeriodMutation,
    useGetHistoryQuery,
    useDownloadCSVNotActivatedLicensesMutation,
    useGetTransactionsListQuery,
    useRetryTransactionsMutation,
    useRetryAllFailedTransactionsMutation,
    useGetIoloLicenseInfoQuery,
    useDisableIoloLicenseMutation,
    useUpdateIoloLicenseExpirationMutation,
    useImportIoloLicenseMutation,
} = api;
